import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Input, Text, Badge, HStack, Flex,
  Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, FormControl, FormLabel, Select, useToast,
  VStack, SimpleGrid, Divider, Alert, AlertIcon, AlertDescription, Checkbox,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdSearch, MdAttachMoney, MdPictureAsPdf } from 'react-icons/md';
import TableSkeleton from '../../components/TableSkeleton';
import juzgadoApi from '../../services/juzgadoApi';

const EMPTY_FACTURA = { rfc: '', nombre: '', codigoPostal: '', regimenFiscal: '616', usoCfdi: 'S01', email: '' };

export default function CajaJuzgado() {
  const [busqueda, setBusqueda] = useState('');
  const [resultados, setResultados] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sancionSel, setSancionSel] = useState<any>(null);
  const [formPago, setFormPago] = useState({ metodoPago: 'Efectivo', folioRecibo: '' });
  const [facturar, setFacturar] = useState(false);
  const [facturaData, setFacturaData] = useState({ ...EMPTY_FACTURA });
  const [saving, setSaving] = useState(false);
  const toast = useToast();

  const abrirCobro = (s: any) => {
    setSancionSel(s);
    setFacturar(false);
    setFacturaData({ ...EMPTY_FACTURA, nombre: s.ciudadano && s.ciudadano !== '—' ? s.ciudadano : '' });
  };

  const buscar = async () => {
    if (!busqueda.trim()) return;
    setLoading(true);
    try {
      const data = await juzgadoApi.listarNotasCobro({ q: busqueda });
      setResultados(data);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePagar = async () => {
    if (!sancionSel || !formPago.metodoPago) return;
    if (facturar && !facturaData.rfc.trim()) {
      return toast({ title: 'RFC requerido para facturar', status: 'warning' });
    }
    setSaving(true);
    try {
      const r = await juzgadoApi.registrarPago(sancionSel.id, {
        monto: sancionSel.montoCobrar,
        metodoPago: formPago.metodoPago,
        folioRecibo: formPago.folioRecibo || undefined,
        facturar,
        facturaData: facturar ? facturaData : undefined,
      });
      if (facturar && r.cfdi) {
        if (r.cfdi.success) {
          toast({ title: 'Pago y CFDI emitidos', description: `UUID: ${r.cfdi.uuid}`, status: 'success', duration: 7000 });
        } else {
          toast({ title: 'Pago registrado, CFDI falló', description: r.cfdi.error ?? 'Error al timbrar', status: 'warning', duration: 8000 });
        }
      } else {
        toast({ title: 'Pago registrado — Caso cerrado', status: 'success', duration: 5000 });
      }
      setSancionSel(null);
      await buscar();
    } catch (e: any) {
      toast({ title: 'Error al registrar pago', description: e.message, status: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const fmtNum = (n?: number) =>
    n != null ? `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '—';

  const fmtFecha = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString('es-MX') : '—';

  return (
    <Box p={4} pt="79px">
      <Text fontSize="2xl" fontWeight="bold" mb={4} color="#5b182b">
        Caja — Sanciones Juzgado Municipal
      </Text>
      <Text fontSize="sm" color="gray.500" mb={4}>
        Busca por nombre, folio de sanción o folio de infracción para cobrar.
      </Text>

      <HStack mb={4}>
        <Input
          placeholder="Folio, nombre del ciudadano..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && buscar()}
          borderColor="#bb8c52"
          _focus={{ borderColor: '#5b182b' }}
          maxW="360px"
        />
        <Button leftIcon={<MdSearch />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={buscar} isLoading={loading}>
          Buscar
        </Button>
      </HStack>

      {loading ? (
        <TableSkeleton rows={5} columns={6} />
      ) : resultados.length === 0 && busqueda ? (
        <Text color="gray.400" textAlign="center" py={8}>Sin resultados para "{busqueda}"</Text>
      ) : (
        <Box bg="white" borderRadius="lg" shadow="sm" overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="#5b182b">
              <Tr>
                <Th color="white">Folio Sanción</Th>
                <Th color="white">Folio Infracción</Th>
                <Th color="white">Ciudadano</Th>
                <Th color="white">Fecha</Th>
                <Th color="white">Monto a cobrar</Th>
                <Th color="white">Descuento PP</Th>
                <Th color="white">Estatus</Th>
                <Th color="white">Acción</Th>
              </Tr>
            </Thead>
            <Tbody>
              {resultados.map(r => (
                <Tr key={r.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="medium" color="#5b182b">{r.folioSancion}</Td>
                  <Td fontSize="sm">{r.folioInfraccion}</Td>
                  <Td>{r.ciudadano}</Td>
                  <Td fontSize="sm">{fmtFecha(r.fechaSancion)}</Td>
                  <Td isNumeric fontWeight="bold" color="#5b182b">{fmtNum(r.montoCobrar)}</Td>
                  <Td>
                    {r.descuentoProntoPago
                      ? <Badge colorScheme="green">50% hasta {fmtFecha(r.fechaLimiteProntoPago)}</Badge>
                      : <Badge colorScheme="gray">No</Badge>}
                  </Td>
                  <Td>
                    <Badge colorScheme={r.estatus === 'PAGADO' ? 'green' : 'orange'}>{r.estatus}</Badge>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      {r.estatus !== 'PAGADO' && (
                        <Button size="xs" leftIcon={<MdAttachMoney />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }}
                          onClick={() => abrirCobro(r)}>
                          Cobrar
                        </Button>
                      )}
                      <Button size="xs" colorScheme="red" variant="outline" as="a"
                        href={juzgadoApi.pdfInformeSancion(Number(r.folioInfraccion))} target="_blank">
                        PDF
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Modal de cobro */}
      <Modal isOpen={!!sancionSel} onClose={() => setSancionSel(null)} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader bg="#5b182b" color="white" borderTopRadius="md">
            Registrar Pago — {sancionSel?.folioSancion}
          </ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody pt={4}>
            {sancionSel && (
              <VStack spacing={3} align="stretch">
                <SimpleGrid columns={2} spacing={3} fontSize="sm">
                  <Box><Text fontWeight="bold" color="#5b182b">Ciudadano</Text><Text>{sancionSel.ciudadano}</Text></Box>
                  <Box><Text fontWeight="bold" color="#5b182b">Folio Infracción</Text><Text>{sancionSel.folioInfraccion}</Text></Box>
                  <Box><Text fontWeight="bold" color="#5b182b">Monto original</Text><Text>{fmtNum(sancionSel.montoOriginal)}</Text></Box>
                  <Box>
                    <Text fontWeight="bold" color="#5b182b">Monto a cobrar</Text>
                    <Text fontSize="lg" fontWeight="bold" color="#5b182b">{fmtNum(sancionSel.montoCobrar)}</Text>
                  </Box>
                </SimpleGrid>
                {sancionSel.descuentoProntoPago && (
                  <Alert status="success" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>Descuento pronto pago del {sancionSel.porcentajeDescPP}% aplicado. Válido hasta {fmtFecha(sancionSel.fechaLimiteProntoPago)}.</AlertDescription>
                  </Alert>
                )}
                <Text fontSize="sm" color="gray.600" fontStyle="italic">{sancionSel.montoALetras}</Text>
                <Divider />
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Método de pago</FormLabel>
                  <Select value={formPago.metodoPago} onChange={e => setFormPago(p => ({ ...p, metodoPago: e.target.value }))} borderColor="#bb8c52">
                    <option value="Efectivo">Efectivo</option>
                    <option value="Tarjeta">Tarjeta</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="Portal en línea">Portal en línea</option>
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Folio de recibo</FormLabel>
                  <Input value={formPago.folioRecibo} onChange={e => setFormPago(p => ({ ...p, folioRecibo: e.target.value }))} borderColor="#bb8c52" _focus={{ borderColor: '#5b182b' }} placeholder="Opcional" />
                </FormControl>

                <Divider />
                <Checkbox isChecked={facturar} onChange={e => setFacturar(e.target.checked)} colorScheme="red">
                  <Text fontSize="sm" fontWeight="semibold">Generar CFDI (factura)</Text>
                </Checkbox>

                {facturar && (
                  <Box p={3} border="1px solid" borderColor="#bb8c52" borderRadius="md">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm">RFC</FormLabel>
                        <Input value={facturaData.rfc} onChange={e => setFacturaData(p => ({ ...p, rfc: e.target.value.toUpperCase() }))} borderColor="#bb8c52" maxLength={13} />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Razón social / Nombre</FormLabel>
                        <Input value={facturaData.nombre} onChange={e => setFacturaData(p => ({ ...p, nombre: e.target.value }))} borderColor="#bb8c52" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Código postal</FormLabel>
                        <Input value={facturaData.codigoPostal} onChange={e => setFacturaData(p => ({ ...p, codigoPostal: e.target.value }))} borderColor="#bb8c52" maxLength={5} placeholder="90160" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Régimen fiscal</FormLabel>
                        <Input value={facturaData.regimenFiscal} onChange={e => setFacturaData(p => ({ ...p, regimenFiscal: e.target.value }))} borderColor="#bb8c52" placeholder="616" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Uso CFDI</FormLabel>
                        <Input value={facturaData.usoCfdi} onChange={e => setFacturaData(p => ({ ...p, usoCfdi: e.target.value }))} borderColor="#bb8c52" placeholder="S01" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Correo (envío de factura)</FormLabel>
                        <Input type="email" value={facturaData.email} onChange={e => setFacturaData(p => ({ ...p, email: e.target.value }))} borderColor="#bb8c52" />
                      </FormControl>
                    </SimpleGrid>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Si no se indica RFC se usará público en general. El CFDI se timbra al confirmar el pago.
                    </Text>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter borderTopWidth={1}>
            <Button variant="ghost" mr={3} onClick={() => setSancionSel(null)}>Cancelar</Button>
            <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} leftIcon={<MdAttachMoney />} isLoading={saving} onClick={handlePagar}>
              Confirmar Pago — {fmtNum(sancionSel?.montoCobrar)}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
